import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { customerAPI, itemsAPI, loanAPI, formatCurrency } from '../../services/api';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { useRates } from '../../contexts/RatesContext';

const CreateLoan = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const [enforceLtv, setEnforceLtv] = useState(true);

  const { data: customersData, isLoading: customersLoading, error: customersError } = useQuery(
    ['customers-min'],
    async () => {
      // Fetch first page with larger limit just for selection list
      const res = await customerAPI.getCustomers({ page: 1, limit: 100, status: 'active' });
      return res?.data?.data?.customers || [];
    },
    { staleTime: 5 * 60 * 1000 }
  );

  // Local state for search input with debounce (min 3 chars)
  const [customerSearch, setCustomerSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(customerSearch.trim());
    }, 300);
    return () => clearTimeout(id);
  }, [customerSearch]);

  // Query for searched customers when >= 3 chars
  const { data: searchedCustomersResp, isFetching: searchingCustomers } = useQuery(
    ['customers-search', debouncedSearch],
    async () => {
      if (!debouncedSearch || debouncedSearch.length < 3) return { data: { data: { customers: [] } } };
      const res = await customerAPI.getCustomers({ page: 1, limit: 50, status: 'active', search: debouncedSearch });
      return res;
    },
    { keepPreviousData: true }
  );

  const searchedCustomers = useMemo(() => {
    const arr = searchedCustomersResp?.data?.data?.customers;
    return Array.isArray(arr) ? arr : [];
  }, [searchedCustomersResp]);

  const customerOptions = useMemo(() => {
    return debouncedSearch && debouncedSearch.length >= 3 ? searchedCustomers : (customersData || []);
  }, [debouncedSearch, searchedCustomers, customersData]);

  // Handle customer selection
  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(`${customer.customer_code} - ${customer.first_name} ${customer.last_name}`);
    setValue('customer_id', customer.id);
    setShowCustomerDropdown(false);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearch(value);
    setShowCustomerDropdown(value.length >= 3);
    if (!value) {
      setSelectedCustomer(null);
      setValue('customer_id', '');
    }
  };

  const { data: categoriesData } = useQuery(
    ['item-categories'],
    async () => {
      const res = await itemsAPI.getCategories();
      return res?.data?.data || [];
    },
    { staleTime: 5 * 60 * 1000 }
  );

  const { getRate } = useRates();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setError,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      customer_id: '',
      loan_amount: '',
      interest_rate: '',
      tenure_months: 12,
      processing_charge: 0,
      total_weight: 0,
      market_value: 0,
      loan_date: new Date().toISOString().slice(0, 10),
      maturity_date: '',
      remarks: '',
      pledged_items: [],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'pledged_items' });

  const loanAmount = parseFloat(watch('loan_amount') || 0) || 0;
  const interestRate = parseFloat(watch('interest_rate') || 0) || 0; // monthly %
  const pledgedItems = useWatch({ control, name: 'pledged_items' }) || [];
  const tenureMonths = parseInt(watch('tenure_months') || 12, 10);

  // Build category map and filter to Gold/Silver
  const goldSilverCategories = useMemo(() => {
    const list = (categoriesData || []).filter((c) => ['Gold', 'Silver'].includes(c.name));
    const byId = {};
    list.forEach((c) => { byId[c.id] = c; });
    return { list, byId };
  }, [categoriesData]);

  // Immediate handlers to update rate/value on category or purity change
  const handleCategoryChange = (index, event) => {
    const categoryId = Number(event?.target?.value);
    const categoryName = goldSilverCategories.byId[categoryId]?.name || 'Gold';
    const metal = categoryName.toLowerCase();
    const allowedPurities = metal === 'silver' ? ['Standard', 'Fine', 'Sterling(92.5%)'] : ['Standard', '18K', '20K', '22K', '24K'];
    let purity = watch(`pledged_items.${index}.purity`);
    if (!allowedPurities.includes(purity)) {
      purity = allowedPurities[0];
      setValue(`pledged_items.${index}.purity`, purity, { shouldDirty: true, shouldValidate: false });
    }
    let rate = 0;
    if (purity === 'Standard') {
      const purities = metal === 'silver' ? ['Fine', 'Sterling(92.5%)'] : ['18K','20K','22K','24K'];
      const vals = purities.map(p=>Number(getRate(metal,p)||0)).filter(v=>v>0);
      rate = vals.length ? Number((vals.reduce((a,b)=>a+b,0)/vals.length).toFixed(2)) : 0;
    } else {
      rate = Number(getRate(metal, purity) || 0);
    }
    setValue(`pledged_items.${index}.rate_per_gram`, Math.max(0, rate), { shouldDirty: true, shouldValidate: false });
    const weight = Number(watch(`pledged_items.${index}.weight`) || 0);
    const value = Number((weight * rate).toFixed(2));
    setValue(`pledged_items.${index}.value`, value, { shouldDirty: true, shouldValidate: false });
  };

  const handlePurityChange = (index, event) => {
    const purity = event?.target?.value;
    const categoryId = Number(watch(`pledged_items.${index}.category_id`));
    const categoryName = goldSilverCategories.byId[categoryId]?.name || 'Gold';
    const metal = categoryName.toLowerCase();
    if (purity === 'Standard') {
      // Always recalc average when selecting Standard
      const purities = metal === 'silver' ? ['Fine', 'Sterling(92.5%)'] : ['18K','20K','22K','24K'];
      const vals = purities.map(p=>Number(getRate(metal,p)||0)).filter(v=>v>0);
      const nextRate = vals.length ? Number((vals.reduce((a,b)=>a+b,0)/vals.length).toFixed(2)) : 0;
      setValue(`pledged_items.${index}.rate_per_gram`, Math.max(0,nextRate), { shouldDirty: true, shouldValidate: false });
    } else {
      const rate = Number(getRate(metal, purity) || 0);
      setValue(`pledged_items.${index}.rate_per_gram`, rate, { shouldDirty: true, shouldValidate: false });
    }
    const weight = Number(watch(`pledged_items.${index}.weight`) || 0);
    const finalRate = Number(watch(`pledged_items.${index}.rate_per_gram`) || 0);
    const value = Number((weight * finalRate).toFixed(2));
    setValue(`pledged_items.${index}.value`, value, { shouldDirty: true, shouldValidate: false });
  };

  const handleWeightChange = (index, event) => {
    const weight = Number(event?.target?.value || 0);
    const categoryId = Number(watch(`pledged_items.${index}.category_id`));
    const categoryName = goldSilverCategories.byId[categoryId]?.name || 'Gold';
    const metal = categoryName.toLowerCase();
    const purity = watch(`pledged_items.${index}.purity`);
    const rate = purity === 'Standard' ? Number(watch(`pledged_items.${index}.rate_per_gram`) || 0) : Number(getRate(metal, purity) || 0);
    const value = Number((weight * rate).toFixed(2));
    setValue(`pledged_items.${index}.value`, value, { shouldDirty: true, shouldValidate: false });
  };

  const handleRateChange = (index, event) => {
    const rate = Math.max(0, Number(event?.target?.value || 0));
    const weight = Number(watch(`pledged_items.${index}.weight`) || 0);
    const value = Number((weight * rate).toFixed(2));
    setValue(`pledged_items.${index}.rate_per_gram`, rate, { shouldDirty: true, shouldValidate: false });
    setValue(`pledged_items.${index}.value`, value, { shouldDirty: true, shouldValidate: false });
  };

  // Auto populate purity defaults, rate and value when category/purity/weight changes
  useEffect(() => {
    (pledgedItems || []).forEach((item, idx) => {
      const categoryName = goldSilverCategories.byId[item.category_id]?.name || 'Gold';
      const metal = categoryName.toLowerCase();
      const allowedPurities = metal === 'silver' ? ['Standard', 'Fine', 'Sterling(92.5%)'] : ['Standard', '18K', '20K', '22K', '24K'];
      let purity = item.purity;

      // If current purity is not valid for selected category, reset to default purity
      if (!allowedPurities.includes(purity)) {
        purity = allowedPurities[0];
        setValue(`pledged_items.${idx}.purity`, purity, { shouldDirty: true, shouldValidate: false });
      }

      // For Standard purity, keep user-entered rate. Otherwise set from settings
      if (purity !== 'Standard') {
        const rate = Number(getRate(metal, purity) || 0);
        if (rate !== (Number(item.rate_per_gram) || 0)) {
          setValue(`pledged_items.${idx}.rate_per_gram`, rate, { shouldDirty: true, shouldValidate: false });
        }
      } else {
        let current = Number(item.rate_per_gram) || 0;
        if (current < 0) current = 0;
        if (current === 0) {
          const purities = metal === 'silver' ? ['Fine', 'Sterling(92.5%)'] : ['18K','20K','22K','24K'];
          const vals = purities.map(p=>Number(getRate(metal,p)||0)).filter(v=>v>0);
          const avg = vals.length ? Number((vals.reduce((a,b)=>a+b,0)/vals.length).toFixed(2)) : 0;
          if (avg !== (Number(item.rate_per_gram) || 0)) {
            setValue(`pledged_items.${idx}.rate_per_gram`, avg, { shouldDirty: true, shouldValidate: false });
            current = avg;
          }
        }
      }

      // Recalculate value every time based on weight × rate
      const weight = Number(item.weight) || 0;
      const currentRate = purity === 'Standard' ? Math.max(0, Number(item.rate_per_gram) || 0) : Number(getRate(metal, purity) || 0);
      const value = Number((weight * currentRate).toFixed(2));
      if (value !== (Number(item.value) || 0)) {
        setValue(`pledged_items.${idx}.value`, value, { shouldDirty: true, shouldValidate: false });
      }
    });
  }, [pledgedItems, goldSilverCategories.byId, getRate, setValue]);

  const totalPledgedValue = useMemo(() => {
    return (pledgedItems || []).reduce((sum, i) => {
      const weight = Number(i.weight) || 0;
      const rate = Number(i.rate_per_gram) || 0;
      return sum + weight * rate;
    }, 0);
  }, [pledgedItems]);

  const totalWeight = useMemo(() => {
    return (pledgedItems || []).reduce((sum, i) => sum + (parseFloat(i.weight) || 0), 0);
  }, [pledgedItems]);

  const maxLTV = 80;
  const ltvRatio = useMemo(() => (totalPledgedValue > 0 ? (loanAmount / totalPledgedValue) * 100 : 0), [loanAmount, totalPledgedValue]);
  const eligible = ltvRatio > 0 && ltvRatio <= maxLTV; // 80% or less is allowed

  // Monthly interest (interest-only) based on current loan amount; compounding is handled server-side
  const estimatedMonthlyInterest = useMemo(() => {
    const r = (interestRate || 0) / 100; // monthly rate as decimal
    if (loanAmount <= 0) return 0;
    return Number((loanAmount * r).toFixed(2));
  }, [loanAmount, interestRate]);

  // Customer loan history and default count
  const selectedCustomerId = watch('customer_id');
  const { data: customerDetails } = useQuery(
    ['customer-details', selectedCustomerId],
    async () => {
      if (!selectedCustomerId) return null;
      const res = await customerAPI.getCustomer(selectedCustomerId);
      return res?.data?.data || null;
    },
    { enabled: !!selectedCustomerId }
  );
  const customerLoans = customerDetails?.loans || [];
  const defaultedCount = customerLoans.filter((l) => l.status === 'defaulted').length;
  const blockedForDefaults = defaultedCount > 2;
  const creditStatus = defaultedCount > 2 ? 'Poor' : (defaultedCount > 0 ? 'Average' : 'Good');

  // Determine if all pledged items are sufficiently filled to evaluate eligibility
  const allItemsFilled = useMemo(() => {
    if (!pledgedItems || pledgedItems.length === 0) return false;
    return pledgedItems.every((i) => (Number(i.weight) || 0) > 0 && (Number(i.rate_per_gram) || 0) > 0);
  }, [pledgedItems]);

  const showIneligibleBanner = (loanAmount > 0) && allItemsFilled && ((enforceLtv && !eligible) || blockedForDefaults);

  const onSubmit = async (form) => {
    // Basic client-side validation
    if (!form.customer_id) {
      setError('customer_id', { type: 'required', message: 'Customer is required' });
      return;
    }
    
    // Validate loan amount is positive
    const loanAmount = parseFloat(form.loan_amount);
    if (isNaN(loanAmount) || loanAmount <= 0) {
      setError('loan_amount', { type: 'validation', message: 'Loan amount must be a positive number' });
      toast.error('Loan amount must be greater than 0');
      return;
    }
    
    // Pledged items required only when enforcing LTV
    if (enforceLtv && (!form.pledged_items || form.pledged_items.length === 0)) {
      toast.error('Add at least one pledged item');
      return;
    }
    if (blockedForDefaults) {
      toast.error('Customer is blocked due to multiple defaults');
      return;
    }
    // LTV is informational on the client; server will validate based on enforce_ltv

    setSubmitting(true);
    try {
      // derive computed fields to satisfy backend validation
      const computedMarketValue = totalPledgedValue;
      const computedTotalWeight = totalWeight;
      const loanDate = form.loan_date ? new Date(form.loan_date) : new Date();
      const maturity = new Date(loanDate);
      const monthsToAdd = parseInt(form.tenure_months || 12, 10);
      maturity.setMonth(maturity.getMonth() + monthsToAdd);
      const maturityStr = maturity.toISOString().slice(0, 10);
      const payload = {
        ...form,
        loan_amount: parseFloat(form.loan_amount),
        interest_rate: parseFloat(form.interest_rate),
        processing_charge: 0,
        enforce_ltv: enforceLtv,
        total_weight: enforceLtv ? Number(computedTotalWeight.toFixed(3)) : 0,
        market_value: enforceLtv ? Number(computedMarketValue.toFixed(2)) : 0,
        maturity_date: maturityStr,
        pledged_items: form.pledged_items.map((i) => {
          const weight = Number(i.weight) || 0;
          const rate = Number(i.rate_per_gram) || 0;
          return {
            ...i,
            weight: weight,
            rate_per_gram: rate,
            value: Number((weight * rate).toFixed(2)),
          };
        }),
      };

      const res = await loanAPI.createLoan(payload);
      toast.success('Loan created successfully');
      const newLoan = res?.data?.data;
      // Refresh lists and related caches
      // Use partial-match key to invalidate all variants of ['loans', ...filters]
      queryClient.invalidateQueries('loans');
      if (newLoan?.id) {
        queryClient.invalidateQueries(['loan', String(newLoan.id)]);
      }
      if (newLoan?.id) navigate(`/loans/${newLoan.id}`);
      else navigate('/loans');
    } catch (e) {
      const resp = e?.response?.data;
      if (Array.isArray(resp?.errors)) {
        resp.errors.forEach((err) => {
          if (err?.field && err?.message) setError(err.field, { type: 'server', message: err.message });
        });
        toast.error('Please fix the highlighted errors');
      } else {
        toast.error(resp?.message || 'Failed to create loan');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to="/loans"
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Loans
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg p-6 space-y-6">
        <h1 className="text-2xl font-bold text-primary-700">Create New Loan</h1>

        {/* Customer Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Search & Select Customer</label>
          <div className="mt-1 relative">
              <input
              className="input w-full"
                placeholder="🔍︎ Search customer (type at least 3 letters/code/mobile)"
                value={customerSearch}
              onChange={handleSearchChange}
              onFocus={() => setShowCustomerDropdown(customerSearch.length >= 3)}
              onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
              />
              {debouncedSearch && debouncedSearch.length > 0 && debouncedSearch.length < 3 && (
                <div className="text-xs text-gray-500 mt-1">Type at least 3 characters to search</div>
            )}
            
            {/* Dropdown with search results */}
            {showCustomerDropdown && customerOptions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {searchingCustomers ? (
                  <div className="p-3 text-sm text-gray-500">Searching...</div>
                ) : (
                  customerOptions.map((customer) => (
                    <div
                      key={customer.id}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      onClick={() => handleCustomerSelect(customer)}
                    >
                      <div className="font-medium text-gray-900">
                        {customer.customer_code} - {customer.first_name} {customer.last_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {customer.mobile_1 && `Mobile: ${customer.mobile_1}`}
                        {customer.father_name && ` • Father: ${customer.father_name}`}
                      </div>
                    </div>
                  ))
              )}
            </div>
            )}
            
            {/* Show message when no results found */}
            {showCustomerDropdown && !searchingCustomers && customerOptions.length === 0 && debouncedSearch.length >= 3 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                <div className="p-3 text-sm text-gray-500">No customers found</div>
            </div>
            )}
          </div>
          {errors.customer_id && <p className="text-sm text-red-600 mt-1">{errors.customer_id.message}</p>}
          {customersError && (
            <p className="text-sm text-red-600 mt-1">Failed to load customers</p>
          )}

          {/* Enhanced Customer Details Card */}
          {selectedCustomerId && customerDetails?.customer && (
            <div className="mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-800">Customer Information</h3>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Active Customer</span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Father Name</div>
                      <div className="font-semibold text-gray-900">{customerDetails.customer.father_name || customerDetails.customer.guardian_name || 'Not provided'}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
              </div>
              <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Mobile</div>
                      <div className="font-semibold text-gray-900">{customerDetails.customer.mobile_1 || 'Not provided'}</div>
                    </div>
                  </div>
                </div>

              <div className="bg-white rounded-lg p-3 border border-gray-100">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-yellow-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">Credit Status</div>
                    <div className={`font-semibold ${creditStatus === 'Good' ? 'text-green-700' : creditStatus === 'Average' ? 'text-yellow-700' : 'text-red-700'}`}>{creditStatus}</div>
                  </div>
                </div>
              </div>

                <div className="bg-white rounded-lg p-3 border border-gray-100 md:col-span-2 lg:col-span-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
              </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Address</div>
                      <div className="font-semibold text-gray-900 text-sm">
                  {[
                    customerDetails.customer.address_line_1,
                    customerDetails.customer.address_line_2,
                    customerDetails.customer.city,
                    customerDetails.customer.state,
                    customerDetails.customer.pincode,
                        ].filter(Boolean).join(', ') || 'Not provided'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
   {/* Enhanced Customer Loan History */}
   {selectedCustomerId && (
          <div className="mt-6 bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Loan History</h3>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${defaultedCount > 2 ? 'bg-red-500' : defaultedCount > 0 ? 'bg-yellow-500' : 'bg-green-500'}`}></div>
                  <span className="text-sm font-medium text-gray-700">
                    {defaultedCount > 2 ? 'High Risk' : defaultedCount > 0 ? 'Medium Risk' : 'Low Risk'}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{defaultedCount}</span> defaults
                </div>
                    </div>
                  </div>
            
            <div className="space-y-3">
              {(customerLoans || []).slice(0, 5).map((loan) => {
                const isDefaulted = loan.status === 'defaulted';
                const isOverdue = loan.status === 'overdue';
                const isActive = loan.status === 'active';
                
                return (
                  <div key={loan.id} className={`bg-white rounded-lg border-l-4 p-4 shadow-sm transition-all hover:shadow-md ${
                    isDefaulted ? 'border-l-red-500 bg-red-50' : 
                    isOverdue ? 'border-l-yellow-500 bg-yellow-50' : 
                    isActive ? 'border-l-green-500 bg-green-50' : 
                    'border-l-gray-300 bg-gray-50'
                  }`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isDefaulted ? 'bg-red-100' : 
                          isOverdue ? 'bg-yellow-100' : 
                          isActive ? 'bg-green-100' : 
                          'bg-gray-100'
                        }`}>
                          <svg className={`w-5 h-5 ${
                            isDefaulted ? 'text-red-600' : 
                            isOverdue ? 'text-yellow-600' : 
                            isActive ? 'text-green-600' : 
                            'text-gray-600'
                          }`} fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                            <text x="12" y="16" textAnchor="middle" fontSize="8" fill="currentColor" fontWeight="bold">₹</text>
                          </svg>
                        </div>
                    <div>
                          <div className="font-semibold text-gray-900">
                            #{loan.loan_number || loan.id}
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(loan.loan_date).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          isDefaulted ? 'bg-red-100 text-red-800' : 
                          isOverdue ? 'bg-yellow-100 text-yellow-800' : 
                          isActive ? 'bg-green-100 text-green-800' : 
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {isDefaulted ? 'In Default' : 
                           isOverdue ? 'Overdue' : 
                           isActive ? 'Active' : 
                           (loan.status || 'Completed')}
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white rounded-lg p-3 border border-gray-100">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-xs text-gray-500 uppercase tracking-wide">Loan Amount</div>
                            <div className="font-semibold text-gray-900 text-lg">
                              {formatCurrency(Number(loan.loan_amount) || 0)}
                            </div>
                          </div>
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white rounded-lg p-3 border border-gray-100">
                        <div className="flex items-center justify-between">
                    <div>
                            <div className="text-xs text-gray-500 uppercase tracking-wide">Outstanding</div>
                            <div className={`font-semibold text-lg ${
                              Number(loan.outstanding_amount) > 0 ? 'text-orange-600' : 'text-green-600'
                            }`}>
                              {formatCurrency(Number(loan.outstanding_amount) || 0)}
                            </div>
                          </div>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            Number(loan.outstanding_amount) > 0 ? 'bg-orange-100' : 'bg-green-100'
                          }`}>
                            <svg className={`w-4 h-4 ${
                              Number(loan.outstanding_amount) > 0 ? 'text-orange-600' : 'text-green-600'
                            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {customerLoans.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-sm">No previous loans found</p>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Loan Details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Loan Amount</label>
            <input 
              type="number" 
              step="0.01" 
              min="0"
              className="input mt-1" 
              {...register('loan_amount', { 
                required: 'Loan amount is required',
                min: { 
                  value: 0.01, 
                  message: 'Loan amount must be greater than 0' 
                },
                validate: (value) => {
                  const numValue = parseFloat(value);
                  if (isNaN(numValue) || numValue <= 0) {
                    return 'Loan amount must be a positive number';
                  }
                  return true;
                }
              })} 
            />
            {errors.loan_amount && <p className="text-sm text-red-600 mt-1">{errors.loan_amount.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Interest Rate (%)</label>
            <select className="input mt-1" {...register('interest_rate', { required: 'Required', valueAsNumber: true })}>
              {[0.5,1.0,1.5,2.0,2.5,3.0,3.5,4.0,4.5,5.0,5.5,6.0].map((r)=> (
                <option key={r} value={r}>{r}%</option>
              ))}
            </select>
            {errors.interest_rate && <p className="text-sm text-red-600 mt-1">{errors.interest_rate.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Loan Date</label>
            <input type="date" className="input mt-1" {...register('loan_date', { required: 'Required' })} />
            {errors.loan_date && <p className="text-sm text-red-600 mt-1">{errors.loan_date.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Tenure (months)</label>
            <select className="input mt-1" {...register('tenure_months', { valueAsNumber: true })}>
              {[3,4,5,6,7,8,9,10,11,12,24,36,48,60].map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-gray-700">Remarks</label>
            <textarea className="input mt-1" rows={3} {...register('remarks')} />
          </div>
        </div>

        {/* Calculations */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-primary-50 rounded p-4">
          <div>
            <div className="text-sm text-gray-500">Total Pledged Value</div>
            <div className="text-lg font-semibold">{formatCurrency(totalPledgedValue || 0)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Total Weight</div>
            <div className="text-lg font-semibold">{(totalWeight || 0).toFixed(3)} g</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Estimated LTV Ratio</div>
            <div className={`text-lg font-semibold ${eligible ? 'text-green-700' : 'text-red-700'}`}>{ltvRatio.toFixed(2)}%</div>
            <div className="text-xs text-gray-500">Max allowed: {maxLTV}%</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Estimated Monthly Interest</div>
            <div className="text-lg font-semibold">{formatCurrency(estimatedMonthlyInterest || 0)}</div>
            <div className="text-xs text-gray-500">Monthly rate {interestRate || 0}% (compounds if unpaid)</div>
          </div>
        </div>

        {/* Eligibility Banner - show only when NOT eligible or blocked and loan amount entered */}
        {showIneligibleBanner && (
          <div className="p-3 rounded bg-red-50 text-red-800">
            {blockedForDefaults ? 'Not Eligible for Loan: Customer has more than 2 defaults' : `Not Eligible for Loan: LTV exceeds ${maxLTV}%`}
          </div>
        )}

        {/* LTV Enforcement Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <input id="enforceLtv" type="checkbox" className="h-4 w-4" checked={enforceLtv} onChange={(e) => setEnforceLtv(e.target.checked)} />
            <label htmlFor="enforceLtv" className="text-sm text-gray-700">Enforce Estimated LTV (max {maxLTV}%)</label>
          </div>
          {!enforceLtv && (
            <div className="text-xs text-gray-500">LTV not enforced; pledged items optional</div>
          )}
        </div>

        {/* Pledged Items */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Pledged Items</h2>
            <button
              type="button"
              onClick={() => append({ category_id: 1, item_name: '', description: '', weight: '', purity: '22K', rate_per_gram: '', value: '' })}
              className="inline-flex items-center px-3 py-1.5 rounded border text-sm bg-white hover:bg-primary-50"
            >
              <PlusIcon className="h-4 w-4 mr-1" /> Add Item
            </button>
          </div>

          {fields.map((field, index) => {
            const categoryRegister = register(`pledged_items.${index}.category_id`, { valueAsNumber: true });
            const purityRegister = register(`pledged_items.${index}.purity`);
            const weightRegister = register(`pledged_items.${index}.weight`, { valueAsNumber: true });
            return (
            <div key={field.id} className="grid grid-cols-1 md:grid-cols-7 gap-3 items-end border rounded p-3">
              <div>
                <label className="block text-xs font-medium text-gray-700">Category</label>
                <select className="input mt-1" {...categoryRegister} onChange={(e) => { categoryRegister.onChange(e); handleCategoryChange(index, e); }}>
                  {(goldSilverCategories.list || []).map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">Item Name</label>
                <input className="input mt-1" {...register(`pledged_items.${index}.item_name`)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">Weight (g)</label>
                <input type="number" step="0.001" className="input mt-1" {...weightRegister} onChange={(e) => { weightRegister.onChange(e); handleWeightChange(index, e); }} />
                {errors.pledged_items?.[index]?.weight && <p className="text-xs text-red-600 mt-1">{errors.pledged_items[index].weight.message}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">Purity</label>
                {(() => {
                  const categoryName = goldSilverCategories.byId[watch(`pledged_items.${index}.category_id`)]?.name || 'Gold';
                  const metal = categoryName.toLowerCase();
                  const options = metal === 'silver'
                    ? ['Standard', 'Fine', 'Sterling(92.5%)']
                    : ['Standard', '18K', '20K', '22K', '24K'];
                  return (
                    <select className="input mt-1" {...purityRegister} onChange={(e) => { purityRegister.onChange(e); handlePurityChange(index, e); }}>
                      {options.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  );
                })()}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">Rate / gram</label>
                {(() => {
                  const categoryName = goldSilverCategories.byId[watch(`pledged_items.${index}.category_id`)]?.name || 'Gold';
                  const metal = categoryName.toLowerCase();
                  const purity = watch(`pledged_items.${index}.purity`);
                  const isStandard = purity === 'Standard';
                  if (isStandard) {
                    return (
                      <input type="number" step="0.01" className="input mt-1" {...register(`pledged_items.${index}.rate_per_gram`, { valueAsNumber: true })} onChange={(e)=>handleRateChange(index,e)} />
                    );
                  }
                  return (
                    <input type="number" step="0.01" readOnly className="input mt-1 bg-gray-100" {...register(`pledged_items.${index}.rate_per_gram`, { valueAsNumber: true })} />
                  );
                })()}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">Value</label>
                <input type="number" step="0.01" readOnly className="input mt-1 bg-gray-100" {...register(`pledged_items.${index}.value`, { valueAsNumber: true })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">Item Photo</label>
                <input type="file" accept="image/*" className="input mt-1" onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => {
                    setValue(`pledged_items.${index}.photo_data`, reader.result, { shouldDirty: true });
                  };
                  reader.readAsDataURL(file);
                }} />
              </div>
              <div className="md:col-span-6">
                <label className="block text-xs font-medium text-gray-700">Description</label>
                <input className="input mt-1" {...register(`pledged_items.${index}.description`)} />
              </div>
              <div className="md:col-span-6 flex justify-end">
                <button type="button" onClick={() => remove(index)} className="inline-flex items-center px-2 py-1 text-sm text-red-700 border border-red-300 rounded hover:bg-red-50">
                  <TrashIcon className="h-4 w-4 mr-1" /> Remove
                </button>
              </div>
            </div>
            );
          })}
        </div>

     

        <div className="flex justify-end">
          <button type="button" className="px-4 py-2 border rounded mr-2" onClick={() => navigate('/loans')}>Cancel</button>
          <button type="button" disabled={submitting || blockedForDefaults} className="px-4 py-2 bg-primary-600 text-white rounded disabled:opacity-50" onClick={handleSubmit(onSubmit)}>
            {submitting ? 'Creating...' : 'Create Loan'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateLoan;

